import { resourceText } from 'src/app/shared/services/localization';
import { languageInfo } from '../../services/localization';
import { readProfilePolicy, applyProfilePolicy, profileFieldDefaults } from 'src/app/shared/profile/profile-field-policy';
import { AddressAutocompleteComponent } from 'src/app/shared/address-check/address-autocomplete.component';
import { formatDate, getLocaleDateTimeFormat } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  ElementRef,
  Input,
  OnInit,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import {
  Form,
  FormArray,
  FormBuilder,
  FormControl,
  FormControlDirective,
  FormGroup,
  NgForm,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CountriesService } from 'src/app/shared/services/countries.service';
import { TenantService } from 'src/app/tenant/tenant.service';
import { Member } from './member.model';
import { Extension, Occupation, Profile, UserId } from './profile.model';
import { ProfileService } from './profile.service';
import { Location } from '@angular/common';
// import { ViewportScroller } from '@angular/common';
import { GlobalService } from '../../services/global.service';
import { MatDatepicker } from '@angular/material/datepicker';
import { fileUploadService } from '../../services/fileUpload.service';
import { AuthService } from 'src/app/core/account/auth.service';
import { IdentityService } from '../../services/identity.service';
import { FormatService } from '../../services/format.service';
import { MemberSearchService } from '../../member-search/member-search.service';
import { MemberService } from '../../services/member.service';
import { take } from 'rxjs/operators';
import { AppService } from '../../services/app.service';
import { User } from 'src/app/core/account/user.model';
import { OccupationService } from './occupation.service';
import { MemberConfiguration } from './member-configuration.model';
import { Key } from 'protractor';

// import { imageThumbnail } from 'image-thumbnail';

@Component({
    selector: 'app-profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.scss'],
    standalone: false
})
export class ProfileComponent implements OnInit, OnDestroy {
  pendingEmailMessage = "";
  emailStatusVersion = '';
  phoneVerificationToken = "";
  emailVerification = false;
  fieldPolicy = {...profileFieldDefaults};
  fieldHidden(key: string) { return this.fieldPolicy?.[key] === 'hidden'; }
  fieldRequired(key: string) { return (this.fieldPolicy?.[key] || profileFieldDefaults[key]) === 'required'; }

  @ViewChild(MatDatepicker) private rangePicker: MatDatepicker<Date>;
  @Input() userId: string;
  @Input() householdSetup = false;
  @Input() householdOverview = false;
  editingPrimary = false;
  get householdEditor() { return this.householdSetup || this.householdOverview; }
  @Output() householdDone = new EventEmitter<void>();
  @ViewChild(AddressAutocompleteComponent) addressAutocomplete: AddressAutocompleteComponent;
  @ViewChild('householdSummary') householdSummary: ElementRef<HTMLElement>;
  @ViewChild('discardEditWarning') discardEditWarning: ElementRef<HTMLElement>;
  householdConfirmation = '';
  private householdScrollTimer: number;
  acknowledgeHouseholdSave(member: Member, action: string) {
    if (!this.householdEditor) return;
    this.householdConfirmation = member.firstName + ' ' + member.lastName + ' has been ' + action + '.';
    clearTimeout(this.householdScrollTimer);
    this.householdScrollTimer = setTimeout(() => {
      const summary = this.householdSummary?.nativeElement;
      summary?.focus({ preventScroll: true });
      summary?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    });
  }
  editingHouseholdMemberId: number = null;
  private pendingMemberPhotos = new Map<Member, { file: File; preview: string }>();
  private pendingMemberPhotoRemovals = new Set<Member>();

  ngOnDestroy() {
    clearTimeout(this.householdScrollTimer);
    this.pendingMemberPhotos.forEach(photo => URL.revokeObjectURL(photo.preview));
  }

  private async uploadPendingMemberPhoto(member: Member) {
    if (this.pendingMemberPhotoRemovals?.has(member)) {
      await this._fileUploadService.deleteFile('profilepics', member.id.toString());
      this.pendingMemberPhotoRemovals.delete(member);
    }
    const photo = this.pendingMemberPhotos.get(member);
    if (!photo) return;
    const uploadedUrl = await this._fileUploadService.uploadFile(photo.file, member.id.toString(), 'profilepics');
    if (!uploadedUrl) throw new Error('Photo upload did not return a URL.');
    member.pictureUrl = this.maskPhoto(uploadedUrl);
    URL.revokeObjectURL(photo.preview);
    this.pendingMemberPhotos.delete(member);
  }

  continuingToApp = false;
  continueAfterDiscard = false;

  finishHouseholdSetup() {
    if (this.memberSaving || this.profileSaving || this.dupeBusy || this.continuingToApp) return;
    const person = this.memberArr.find(member => this.editingPrimary ? member.isPrimary : member.id === this.editingHouseholdMemberId);
    const changed = person && this.editedPersonSnapshot && JSON.stringify(person) !== this.editedPersonSnapshot;
    if ((this.editingPrimary && this.profileForm.dirty) || changed || this.newMemberNotSaved || this.pendingMemberPhotos.size || this.pendingMemberPhotoRemovals.size) {
      this.continueAfterDiscard = true;
      this.showDiscardEditWarning();
      return;
    }
    this.continuingToApp = true;
    this.householdDone.emit();
  }

  keepEditing() {
    this.confirmDiscardEdit = false;
    this.continueAfterDiscard = false;
  }

  isHouseholdPersonEditing(person: Member): boolean {
    return person.isPrimary ? this.editingPrimary : this.editingHouseholdMemberId === person.id;
  }

  editHouseholdPerson(person: Member) {
    if (this.memberSaving || this.profileSaving || this.dupeBusy) return;
    if (this.isHouseholdPersonEditing(person)) {
      this.requestCancelHouseholdEdit();
      return;
    }
    if (this.newMemberNotSaved || this.editingPrimary || this.editingHouseholdMemberId !== null) return;
    this.householdConfirmation = '';
    this.editedPersonSnapshot = JSON.stringify(person);
    this.editingPrimary = person.isPrimary;
    this.editingHouseholdMemberId = person.isPrimary ? null : person.id;
  }

  householdRole(member: Member): string {
    if (member.isPrimary) {
      const label = this.isAdmin === 'true' ? 'Account holder' : 'You';
      return label + (this.editingPrimary ? ' \u00b7 Editing' : this.isAdmin === 'true' ? '' : ' \u00b7 Primary member');
    }
    const roles = { 1: ['SPOUSE', 'Spouse'], 2: ['CHILD', 'Child'], 3: ['OTHER', 'Other'], 4: ['SIBLING', 'Sibling'], 5: ['PARENT', 'Parent'], 6: ['RELATIVE', 'Relative'] };
    const role = roles[member.relationship];
    const label = role ? this.globalService.Get(role[0]) || role[1] : 'Household member';
    return label + (this.editingHouseholdMemberId === member.id ? ' \u00b7 Editing' : '');
  }

  setMemberCommunication(member: Member, field: 'disableText' | 'disableEmail', enabled: boolean) {
    member[field] = !enabled;
  }

  setPrimaryCommunication(field: 'disableText' | 'disableEmail', enabled: boolean) {
    this.profileForm.get(field).setValue(!enabled);
    this.profileForm.markAsDirty();
  }

  confirmDiscardEdit = false;
  private editedPersonSnapshot: string;

  private showDiscardEditWarning() {
    this.confirmDiscardEdit = true;
    clearTimeout(this.householdScrollTimer);
    this.householdScrollTimer = setTimeout(() => {
      const warning = this.discardEditWarning?.nativeElement;
      warning?.focus({ preventScroll: true });
      warning?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
    });
  }

  requestCancelHouseholdEdit(memberForm?: NgForm) {
    this.continueAfterDiscard = false;
    if (this.memberSaving || this.profileSaving) return;
    const person = this.memberArr.find(member => this.editingPrimary ? member.isPrimary : member.id === this.editingHouseholdMemberId);
    const personChanged = person && this.editedPersonSnapshot && JSON.stringify(person) !== this.editedPersonSnapshot;
    if ((this.editingPrimary && this.profileForm.dirty) || memberForm?.dirty || personChanged || this.newMemberNotSaved || this.pendingMemberPhotos.size || this.pendingMemberPhotoRemovals.size) {
      this.showDiscardEditWarning();
      return;
    }
    this.cancelHouseholdEdit();
  }

  async cancelHouseholdEdit() {

    if (this.memberSaving || this.profileSaving) return;
    if (this.continueAfterDiscard) {
      this.continuingToApp = true;
      this.pendingMemberPhotos.forEach(photo => URL.revokeObjectURL(photo.preview));
      this.pendingMemberPhotos.clear();
      this.pendingMemberPhotoRemovals.clear();
      this.confirmDiscardEdit = false;
      this.householdDone.emit();
      return;
    }
    this.confirmDiscardEdit = false;
    this.editedPersonSnapshot = null;
    this.pendingMemberPhotos.forEach(photo => URL.revokeObjectURL(photo.preview));
    this.pendingMemberPhotos.clear();
    this.pendingMemberPhotoRemovals.clear();
    await this.LoadMembers();
    if (this.editingPrimary) {
      await this.LoadProfile();
      this.profileForm.markAsPristine();
    }
    this.editingPrimary = false;
    this.newMemberNotSaved = false;
    this.editingHouseholdMemberId = null;
  }
  @Input() isAdmin: string;
  @Input() showAccountHolder: boolean = false;
  @Input() showMembers: string = 'false';
  @Input() allowEdit: boolean = false;
  @Input() showHeader: boolean = false;
  @Input() shortDate: boolean = true;
  @Input() header: string;
  @Input() showCloseButton: boolean = false;
  minDate: Date = new Date(1900, 1, 1);
  maxDate: Date = new Date();
  user: User;
  verificationCode: string = '';
  isProfLoading: boolean = true;
  isMemberLoading: boolean = true;
  isConfigurationLoading: boolean = true;
  accountActivated: boolean = false;
  originalPhoneNumber: string;
  sendCodeValue: string = '';
  phoneConfirmed: boolean = false;
  codeSent: boolean = false;
  error: string = null;
  memberError: string = null;
  confirmDelete: boolean = false;
  message: string = null;
  memberMessage: string = null;
  showMemberStatusTip: boolean = false;
  profileForm: FormGroup = new FormGroup({});
  memberForm: FormGroup = new FormGroup({});
  submit: boolean = false;
  submitMember: boolean = false;
  isSuccess: boolean = false;
  isMemberSuccess: boolean = false;
  phoneVerificationClosed: boolean = true;
  successClosed: boolean = true;
  memberSuccessClosed: boolean = true;
  failureClosed: boolean = true;
  memberFailureClosed: boolean = true;
  newMemberNotSaved: boolean = false;
  memberDeleting: boolean = false;
  memberSaving: boolean = false;
  codeSending: boolean = false;
  profileSaving: boolean = false;
  recommendationId = 0;
  householdCandidates: any[] = [];
  householdDupes: any[] = [];
  dupeBusy = false;
  dupeError = '';
  async reviewHouseholdDupe(pair: any, different: boolean) {
    if (this.dupeBusy || this.memberSaving || this.profileSaving || this.editingPrimary || this.editingHouseholdMemberId != null || this.newMemberNotSaved) return;
    this.dupeBusy = true; this.dupeError = '';
    try {
      await this.profileService.resolveHouseholdDupe(pair.member1.id, pair.member2.id, different);
      await this.LoadMembers();
      this.householdDupes = await this.profileService.householdDupes(this.userId);
    } catch (error) { this.dupeError = error?.error?.message || resourceText('UI_THE_RECORDS_COULD_NOT_BE_COMBINED_YOUR_INFORMATION_HAS_BEEN_KEPT_PLEASE_RETRY_OR_CONTACT_YOUR_CHURCH_ADMINISTR'); }
    finally { this.dupeBusy = false; }
  }
  deferHouseholdDupe(pair: any) { this.householdDupes = this.householdDupes.filter(item => item !== pair); }
  async refreshHouseholdDupes() {
    if (!this.householdEditor || !this.profileService.householdDupes) return;
    try { this.householdDupes = await this.profileService.householdDupes(this.userId); }
    catch { this.dupeError = resourceText('UI_POSSIBLE_DUPLICATE_REVIEW_COULD_NOT_BE_LOADED_YOU_CAN_CONTINUE_AND_REVIEW_IT_LATER'); }
  }

  recommendationMessage = '';
  recommendationClosed = true;
  recommendationForm: any;
  recommendationMember: Member;
  curIndex: number = -1;
  memberArr: Member[];
  churchName: string = '';
  subdomain: string = '';
  dial_code: string = '';
  languages: string[] = [];
  defaultLanguage: string = 'English';
  pictureUrl: string = 'assets/images/avatar.png';
  origProfileMembership: boolean = false;
  countriesArr: { name: string; dial_code: string; code: string }[];
  defaultCountry: string = '';
  isFromRegistration: boolean = false;
  workflow: boolean = false;
  adminAccess: boolean = false;
  primaryNoteCount: number = 0;
  noteAccessLimitation: string = '';
  primaryMemberId: number = 0;
  disableText: boolean = false;
  disableEmail: boolean = false;
  subscriptionError: boolean = false;
  phoneNumber: string = "";
  originalEmail: string = "";
  activationNeeded: boolean = false;
  occupations: Occupation[] = [];
  activationSent: boolean = false;
  sendingActivation: boolean = false;
  closeActivationSent: boolean = false;
  isSelf: boolean = false;
  profileExtensions: Extension[] = [];
  memberExtensions: Extension[] = [];
  memberConfigurations: MemberConfiguration[] = [];
  addingHouseholdMember: boolean = false;
  confirmUnsubscribe: boolean = false;
  profileUnsubscribe: boolean = false;

  private profileSub: Subscription;
  private setProfileSub: Subscription;
  private setMemberSub: Subscription;
  private deleteMemberSub: Subscription;
  private memberSub: Subscription;
  private addMemberSub: Subscription;
  @Output('opened') openedStream: EventEmitter<void>;

  constructor(
    public profileService: ProfileService,
    // private authService: AuthService,
    private countries: CountriesService,
    private tenantService: TenantService,
    private _router: Router,
    private _location: Location,
    private activatedRoute: ActivatedRoute,
    // private viewportScroller: ViewportScroller,
    private globalService: GlobalService,
    private _fileUploadService: fileUploadService,
    private _authService: AuthService,
    private _identityService: IdentityService,
    private _formatService: FormatService,
    private appService: AppService,
    private authService: AuthService,
    private occupationService: OccupationService,
    private fb: FormBuilder
  ) { }

  // ngOnDestroy(): void {
  //   if (this.memberSub != null) this.memberSub.unsubscribe();
  //   if (this.profileSub != null) this.profileSub.unsubscribe();
  //   if (this.setProfileSub != null) this.setProfileSub.unsubscribe();
  //   if (this.addMemberSub != null) this.addMemberSub.unsubscribe();
  //   if (this.setMemberSub != null) this.setMemberSub.unsubscribe();
  //   if (this.deleteMemberSub != null) this.deleteMemberSub.unsubscribe();
  // }

  async ngOnInit(): Promise<void> {
    this.globalService.selectedOption = "";
    this.profileService.isLoading = true;
    await this.authService.autoLogin(1);
    var Tenant = await this.tenantService.getTenantData();
    this.fieldPolicy = readProfilePolicy(Tenant.profileFieldPolicy);
    this.emailVerification = Tenant.accountVerificationMethod === "email";
    if (this.emailVerification) this.fieldPolicy.email = "required";
    if (this._identityService.hasAdmin() == true)
      this.shortDate = false;
    this.defaultLanguage = Tenant.language;
    if (Tenant.deleted == true) {
      this._router.navigate(['/service-notfound']);
      return;
    }
    // this.globalService.isBack = true;
    this.churchName = Tenant.name;
    this.subdomain = Tenant.subdomain;
    this.workflow = Tenant.membershipWorkflow;
    this.countriesArr = this.countries.CountriesList;
    this.languages = await this.globalService.getLanguages();
    await this.InitializeProfileForm();
    applyProfilePolicy(this.profileForm, this.fieldPolicy);
    this.occupations = await this.occupationService.getOccupationList();

    this.memberConfigurations = await this.profileService.getMemberConfiguration();
    this.memberConfigurations = this.generateOptions();

    await this.LoadProfile();
    await this.LoadMembers();
    await this.refreshHouseholdDupes();
//    this.globalService.title.push(this.globalService.screenHeader);


    this.user = await this.authService.autoLogin(1);
    if (this.user != null) {
      this._identityService.loadUser(this.user);
      this.isSelf = this.user.id == this.userId;
      this.adminAccess =
        this._identityService.permissions.indexOf('Membership Requests') >=
        0 ||
        this._identityService.permissions.indexOf('Membership Management') >=
        0 ||
        this._identityService.permissions.indexOf('Admin') >= 0 ||
        this._identityService.permissions.indexOf('Super Admin') >= 0;
    }
  }

  // ngAfterViewInit() {
  //   setTimeout(() => {
  //   this.globalService.screenHeader = this.globalService.Get(this.header);
  //   });
  // }

  generateOptions() {
    let formExtensions = <FormArray>this.profileForm.controls.extensions;
    if (this.memberConfigurations != null && this.memberConfigurations.length > 0)
      this.memberConfigurations.forEach(a => {
        let selectable: boolean = a.options.indexOf(";") >= 0;
        var extension = this.fb.group({
          id: a.id,
          label: [a.label],
          options: selectable == true ? [a.options.split(';').map(item => item.trim())] : null,
          value: ['', a.mandatory == true ? Validators.required : Validators.nullValidator]
        });
        formExtensions.push(extension);
        if (a.options.indexOf(";") >= 0)
          a['optionsList'] = a.options.split(';').map(item => item.trim());
      });

    this.isConfigurationLoading = false;
    this.profileService.isLoading =
      this.isProfLoading || this.isMemberLoading || this.isConfigurationLoading;
    //if (this.profileService.isLoading == false) this.scrollNow();
    return this.memberConfigurations;
  }

  generateMemberOptions(member: Member) {
    if (this.memberConfigurations != null && this.memberConfigurations.length > 0) {
      this.memberConfigurations.forEach(a => {
        if (a.level >= 2)
          if (a.options.indexOf(";") >= 0)
            a['optionsList'] = a.options.split(';').map(item => item.trim());
      });

      let validIds = this.memberConfigurations.filter(x => x.level >= 2).map(a => a.id);
      if (member.extensionValues == null)
        member.extensionValues = [];
      // this.memberArr.forEach(a => {
        this.memberConfigurations.forEach(b => {
          if (b.level >= 2)
          {
            var extensionFound = member.extensionValues?.filter(x => x.key == b.id);
            if (extensionFound.length > 0)
            {
              extensionFound[0]['key'] = b.id;
              extensionFound[0]['label'] = b.label;
              extensionFound[0]['mandatory'] = b.mandatory;
              extensionFound[0]['optionsList'] = b['optionsList'];
              // extensionFound[0]['value'] = '';
            }
            else
            {
              let newExtension: Extension = {
                key: b.id,
                value: ""
              };
              newExtension['key'] = b.id;
              newExtension['label'] = b.label;
              newExtension['mandatory'] = b.mandatory;
              newExtension['optionsList'] = b['optionsList'];
              newExtension['value'] = '';
              member.extensionValues.push(newExtension);
            }
          }
          member.extensionValues = member.extensionValues.filter(y => validIds.indexOf(y.key) >= 0);
        });
      // });
    }
  }

  markIdValidity(myForm: NgForm, validity: boolean) {
    if (validity == true)
    {
      myForm.controls['id'].clearValidators();
      myForm.controls['id'].updateValueAndValidity();
    }
    else
      myForm.controls['id'].setErrors({ 'customError': true });
  }

  extensionChange(memberForm: NgForm, member: Member)
  {
    this.isMemberSuccess = false;
    this.markIdValidity(memberForm, this.extensionValid(member.extensionValues));
    memberForm.form.markAsDirty();
    memberForm.form.markAsTouched();
  }

  async resetPicture(index, memberForm?: NgForm) {
    if (this.memberSaving || this.profileSaving) return;
    const member = this.memberArr[index];
    const pending = this.pendingMemberPhotos.get(member);
    if (pending) { URL.revokeObjectURL(pending.preview); this.pendingMemberPhotos.delete(member); }
    if (member?.id === 0 || this.householdEditor) {
      if (member.id > 0) this.pendingMemberPhotoRemovals.add(member);
      member.pictureUrl = 'assets/images/avatar.png';
      if (member.isPrimary) this.pictureUrl = member.pictureUrl;
      if (member.isPrimary) this.profileForm.markAsDirty();
      else memberForm?.form.markAsDirty();
      return;
    }
    if (this.subdomain == 'demo') return;
    var memberId = this.memberArr.filter((a) => a.isPrimary == true)[0].id;
    if (index > 0) memberId = this.memberArr[index].id;
    await this._fileUploadService.deleteFile(
      'profilepics',
      memberId.toString()
    );
    if (index == 0) this.pictureUrl = 'assets/images/avatar.png';
    else this.memberArr[index].pictureUrl = 'assets/images/avatar.png';
  }
  async onFileSelectedPrimary(event, index, memberForm?: NgForm) {
    if (this.memberSaving || this.profileSaving) return;
    const file = event.target.files?.[0];
    if (!file) return;
    const member = this.memberArr[index];
    if (member?.id === 0 || this.householdEditor) {
      const previous = this.pendingMemberPhotos.get(member);
      if (previous) URL.revokeObjectURL(previous.preview);
      const preview = URL.createObjectURL(file);
      this.pendingMemberPhotoRemovals?.delete(member);
      this.pendingMemberPhotos.set(member, { file, preview });
      if (member.isPrimary) this.profileForm.markAsDirty();
      else memberForm?.form.markAsDirty();
      member.pictureUrl = preview;
      if (member.isPrimary) this.pictureUrl = preview;
      event.target.value = '';
      return;
    }
    if (this.subdomain == 'demo') return;
    const fd = new FormData();
    var memberId = this.memberArr.filter((a) => a.isPrimary == true)[0].id;
    if (index > 0) memberId = this.memberArr[index].id;
    // var thumbnailed = await imageThumbnail(event.target.files[0]);
    var Url = await this._fileUploadService.uploadFile(
      event.target.files[0],
      memberId.toString(),
      'profilepics'
    );
    if (index == 0) this.pictureUrl = this.maskPhoto(Url);
    else this.memberArr[index].pictureUrl = this.maskPhoto(Url);
    event.target.value = '';
  }

  async AcceptRequest(memberId: number, index: number) {
    await this.profileService.acceptRequest(memberId);
    this.memberArr[index].requestDescription = '';
    this.memberArr[index].isMember = !this.memberArr[index].isMember;
    if (index == 0) {
      this.profileForm.patchValue({
        isMember: this.memberArr[index].isMember ? 1 : 2,
      });
    }
  }

  async DenyRequest(memberId: number, index: number) {
    await this.profileService.denyRequest(memberId);
    this.memberArr[index].requestDescription = '';
  }

  OnCountryChange() {
    var country = this.profileForm.get('country').value;
    this.dial_code = this.countries.CountriesList.filter(
      (a) => a.code == country
    )[0].dial_code;
  }

  async LoadProfile() {
    if (this.profileSub != null) this.profileSub.unsubscribe();
    this.codeSent = await this.profileService.isPending(this.userId);
    var myId: UserId = { id: this.userId };
    await this.profileService
      .getProfile(myId)
      .then(async (data) => {
        this.originalEmail = data.email;
        this.profileExtensions = data.extensionValues;
        this.accountActivated = data.emailConfirmed;
        this.phoneNumber = data.phoneNumber;
        this.phoneConfirmed = data.phoneNumberConfirmed;
        this.phoneVerificationClosed = this.phoneConfirmed;
        this.pictureUrl =
          data.pictureUrl == null || data.pictureUrl == ''
            ? 'assets/images/avatar.png'
            : data.pictureUrl;
        // this.pictureUrl = this.maskPhoto(this.pictureUrl);
        this.defaultCountry = data.country;
        this.dial_code = data.dial_code;
        if (data.language == null || data.language == '')
          data.language = this.defaultLanguage;
        data.language = languageInfo(data.language).name;
        await this.loadAccountHolder(data);
        this.isProfLoading = false;
        this.profileService.isLoading =
          this.isProfLoading || this.isMemberLoading || this.isConfigurationLoading;
//        if (this.profileService.isLoading == false) this.scrollNow();
      })
      .catch((errorMessage) => {
        this.error = errorMessage.message;
        this.failureClosed = false;
        this.isProfLoading = false;
        this.profileService.isLoading =
          this.isProfLoading || this.isMemberLoading || this.isConfigurationLoading;
//        if (this.profileService.isLoading == false) this.scrollNow();
      });
  }

  async SendCode() {
    try {
    this.closeErrors();
    this.codeSending = true;
    this.failureClosed = true;
    var result = await this.profileService.sendCode(this.userId);
    if (result.message == '') {
      this.codeSent = true;
    } else {
      this.error = result.message;
      this.failureClosed = false;
    }
    this.codeSending = false;

    } finally {
      this.codeSending = false;
    }
  }

  async resendActivationEmail() {
    try {
    this.sendingActivation = true;
    await this.profileService.resendActivationEmail(this.userId);
    this.activationSent = true;
    this.sendingActivation = false;

    } finally {
      this.sendingActivation = false;
    }
  }

  async VerifyCode() {
    try {
    this.closeErrors();
    this.codeSending = true;
    this.failureClosed = true;
    var result = await this.profileService.verifyCode(
      this.userId,
      this.verificationCode
    );
    if (result.message == '') {
      this.phoneConfirmed = true;
      this.phoneVerificationClosed = true;
      this.verificationCode = '';
      this.successClosed = false;
      this.message = resourceText('PHONE_VERIFIED_1BF655D');
    } else {
      this.error = result.message;
      this.codeSent = false;
      this.verificationCode = '';
      this.phoneConfirmed = false;
      this.failureClosed = false;
    }
    this.codeSending = false;

    } finally {
      this.codeSending = false;
    }
  }

  sortByPrimaryAndName(a, b) {
    if (a['isPrimary'] == true) return -2;
    if (b['isPrimary'] == true) return 1;
    return a['firstName'] > b['firstName'] ? 1 : -1;
  }

  GetPrimaryShortDate(): string {
    if (!this.profileForm.get('dateOfBirth').value) return resourceText('NOT_PROVIDED');
    var dateString = this.profileForm
      .get('dateOfBirth')
      .value.toString()
      .replaceAll('-', '/');
    var month = new Date(dateString).getMonth() + 1;
    var day = new Date(dateString).getDate();
    return (
      month.toString().padStart(2, '0') + '/' + day.toString().padStart(2, '0')
    );
  }

  GetMemberShortDate(date: Date): string {
    if (!date) return resourceText('NOT_PROVIDED');
    return (
      (new Date(date).getMonth() + 1).toString().padStart(2, '0') +
      '/' +
      new Date(date).getDate().toString().padStart(2, '0')
    );
  }

  clearCellPhone() {
    this.phoneNumber = "";
    this.phoneConfirmed = false;
    this.profileForm.markAsDirty();
  }

  ConfirmCellPhone(phoneNumber) {
    this.phoneNumber = phoneNumber;
    this.phoneConfirmed = true;
  }

  failedVerification() {
    this.phoneNumber = "";
    this.phoneConfirmed = false;
  }

  async notesAccess(): Promise<string> {
    if (this.isAdmin == 'false') return '';
    if (
      this._identityService.permissions.indexOf('Super Admin') >= 0 ||
      this._identityService.permissions.indexOf('Admin') >= 0 ||
      this._identityService.permissions.indexOf('Notes Full Access') >= 0
    )
      return 'FULL';
    if (
      this._identityService.permissions.indexOf('Notes Limited Access') ||
      this._identityService.permissions.indexOf('Leader') >= 0
    ) {
      let memberId = await this._identityService.getMemberId(
        this.globalService.currentUserId
      );
      return memberId.toString();
    }
    return '';
  }

  isLimited() {
    if (this._identityService.permissions.indexOf('Full Profile for Leaders') >= 0)
      return false;
    if (this.isAdmin == 'false') return false;
    return (
      this._identityService.permissions.indexOf('Super Admin') < 0 &&
      this._identityService.permissions.indexOf('Admin') < 0 &&
      this._identityService.permissions.indexOf('Profile Editor') < 0 &&
      this._identityService.permissions.indexOf('Membership Management') < 0 &&
      this._identityService.permissions.indexOf('Membership Requests') < 0 &&
      this._identityService.permissions.indexOf('Profile Viewer') < 0
    );
  }

  async LoadMembers() {
    this.noteAccessLimitation = await this.notesAccess();
    await this.profileService
      .getMembers(this.userId, this.noteAccessLimitation, this.isLimited())
      .then(async (data) => {
        this.memberArr = await data.sort(this.sortByPrimaryAndName);
        this.maskMembers();
        await this.memberArr.map((a) => {
          a['isMemberId'] = a.isMember == true ? 1 : 2;
          a['origIsMember'] = a.isMember;
          a['pictureUrl'] =
            a['pictureUrl'] == ''
              ? 'assets/images/avatar.png'
              : a['pictureUrl'];
        });
        var primaryMember = this.memberArr.filter(
          (a) => a.isPrimary == true
        )[0];
        if (primaryMember != null) {
          this.primaryNoteCount = this.memberArr.filter(
            (a) => a.isPrimary == true
          )[0].noteCount;
          this.primaryMemberId = this.memberArr.filter(
            (a) => a.isPrimary == true
          )[0].id;
          this.disableEmail = primaryMember.disableEmail;
          this.disableText = primaryMember.disableText;
        }
        this.memberArr.forEach(m => {this.generateMemberOptions(m)});
        this.isMemberLoading = false;
        this.profileService.isLoading =
          this.isProfLoading || this.isMemberLoading || this.isConfigurationLoading;
//        if (this.profileService.isLoading == false) this.scrollNow();
      })
      .catch((errorMessage) => {
        this.error = errorMessage.message;
        this.failureClosed = false;
        this.isMemberLoading = false;
        this.profileService.isLoading =
          this.isProfLoading || this.isMemberLoading || this.isConfigurationLoading;
//        if (this.profileService.isLoading == false) this.scrollNow();
      });
  }

  showNote(memberId: number) {
    if (this.showMembers == 'true') {
      if (this.noteAccessLimitation == 'FULL') return true;
      if (this.noteAccessLimitation != '' && memberId != 0) return true;
    }
    return false;
  }

  InitializeProfileForm() {
    this.profileForm = new FormGroup({
      firstName: new FormControl(null, [
        Validators.required,
        Validators.minLength(2),
      ]),
      lastName: new FormControl(null, [
        Validators.required,
        Validators.minLength(2),
      ]),
      email: new FormControl(''),
      phoneNumber: new FormControl(null, Validators.pattern('[0-9]*')),
      street: new FormControl(null, [Validators.required]),
      city: new FormControl(null, [Validators.required]),
      state: new FormControl(null, [Validators.required]),
      zip: new FormControl(null, [Validators.required]),
      country: new FormControl(this.defaultCountry, [Validators.required]),
      isMember: new FormControl(null, [Validators.required]),
      gender: new FormControl(null, [Validators.required]),
      maritalStatus: new FormControl(null),
      occupation: new FormControl(null),
      dateOfBirth: new FormControl(null, [
        Validators.required,
      ]),
      language: new FormControl(this.defaultLanguage),
      disableText: new FormControl(false),
      disableEmail: new FormControl(false),
      extensions: this.fb.array([]),
      createdBy: new FormControl({value:null,disabled:true}),
      creationDate: new FormControl({value:null,disabled:true})
    });
  }

  get extensions(): FormArray {
    return this.profileForm.get('extensions') as FormArray;
  }

  maskdata(val: string): string {
    if (!val) return val;
    if (this.subdomain == 'demo') return val[0].toUpperCase() + 'XXXXXXXXX';
    return val;
  }

  maskdate(val: Date): Date {
    if (this.subdomain == 'demo') return new Date('01/01/1000');
    return val;
  }

  maskPhoto(url: string): string {
    // if (this.subdomain == 'demo') return 'assets/images/avatar.png';
    return url;
  }

  maskMembers() {
    if (this.subdomain == 'demo')
      this.memberArr.forEach((a) => {
        a.lastName = this.maskdata(a.lastName);
        a.dateOfBirth = this.maskdate(a.dateOfBirth);
        // a.pictureUrl = 'assets/images/avatar.png';
      });
  }

  loadAccountHolder(data: Profile) {
    this.profileForm.setValue({
      firstName: data.firstName,
      lastName: this.maskdata(data.lastName),
      email: this.maskdata(data.email),
      dateOfBirth: data.dateOfBirth ? formatDate(this.maskdate(data.dateOfBirth), 'yyyy-MM-dd', 'en') : null,
      street: this.maskdata(data.street),
      city: this.maskdata(data.city),
      state: this.maskdata(data.state),
      zip: this.maskdata(data.zip),
      country:
        data.country == null || data.country == ''
          ? this.defaultCountry
          : data.country,
      isMember: data.isMember ? 1 : 2,
      gender: data.gender,
      maritalStatus: data.maritalStatus,
      occupation: data.occupation == 0 ? null : data.occupation,
      language: data.language,
      disableEmail: this.disableEmail,
      disableText: this.disableText,
      phoneNumber: this.maskdata(data.phoneNumber),
      extensions: this.loadExtensions().value,
      createdBy: data.createdBy,
      creationDate: data.creationDate
    });

    if (data.country == null || data.country == '')
      this.dial_code = this.countries.CountriesList.filter(
        (a) => a.code == this.defaultCountry
      )[0].dial_code;
    else
      this.dial_code = this.countries.CountriesList.filter(
        (a) => a.code == data.country
      )[0].dial_code;
    this.originalPhoneNumber = data.phoneNumber;
    this.origProfileMembership = data.isMember;
    this.phoneConfirmed = data.phoneNumberConfirmed;
  }

  loadExtensions(): FormArray {
    let formArray = this.profileForm.get("extensions") as FormArray;
    formArray.controls.forEach(control => {
      let id = control.get("id").value;
      let value = this.profileExtensions == null || this.profileExtensions.filter(a => a.key == id) == null
       || this.profileExtensions.filter(a => a.key == id).length == 0 ?
      "": this.profileExtensions.filter(a => a.key == id)[0].value;
      control.get("value").setValue(value);
    });
    return formArray;
  }

  closeErrors() {
    this.failureClosed = true;
    this.successClosed = true;
  }

  async requestMembership(userId: string, memberList: number[]) {
    await this.profileService.requestMembership(userId, memberList);
  }

  async cancelMembership(userId: string, memberList: number[]) {
    await this.profileService.cancelMembership(userId, memberList);
  }

  async onSubmit() {
    try {
    if (this.profileSaving || this.memberSaving) return;
    if (this.confirmUnsubscribe == true || this.profileUnsubscribe == true)
      return;
    this.submit = true;
    this.closeErrors();
    this.isMemberSuccess = false;
    if (this.profileForm.valid == false || this.subdomain == 'demo') return;
    if (this.fieldRequired('picture') && (!this.pictureUrl || this.pictureUrl.includes('assets/images/avatar.png'))) { this.error = resourceText('PLEASE_ADD_YOUR_PROFILE_PICTURE'); this.failureClosed = false; return; }

    if (!this.emailVerification && (this.phoneConfirmed == false || this.phoneNumber.trim() == "") && (this.isAdmin == 'false')) {
      this.error = resourceText('VERIFYPHONE_TO_SAVE_47CDCAA');
      this.failureClosed = false;
      this.profileSaving = false;
      this.submit = true;
      return;
    }


    // If workflow then request membership change, instead of actually changing it
    var memberList: number[] = [];
    memberList.push(this.memberArr[0].id);
    var curMembership = this.profileForm.get('isMember').value == 1;

    if (
      this.workflow == true &&
      this.adminAccess == false &&
      this.origProfileMembership != curMembership
    ) {
      if (curMembership == true) {
        this.requestMembership(this.userId, memberList);
        this.profileForm.patchValue({ isMember: 2 });
        this.memberArr[0].requestDescription = 'MEMBERSHIP_REQUEST_SUBMITTED';
      } else {
        this.cancelMembership(this.userId, memberList);
        this.profileForm.patchValue({ isMember: 1 });
        this.memberArr[0].requestDescription =
          'MEMBERSHIP_CANCELLATION_SUBMITTED';
      }
    }

    const profile: Profile = {
      id: this.userId,
      firstName: this.profileForm.get('firstName').value.trim(),
      lastName: this.profileForm.get('lastName').value.trim(),
      email: (this.profileForm.get('email').value || '').trim(),
      dateOfBirth: this.profileForm.get('dateOfBirth').value,
      street: (this.profileForm.get('street').value || '').trim(),
      addressVerificationToken: '',
      city: (this.profileForm.get('city').value || '').trim(),
      state: (this.profileForm.get('state').value || '').trim(),
      zip: (this.profileForm.get('zip').value || '').trim(),
      country: this.profileForm.get('country').value,
      gender: this.profileForm.get('gender').value,
      dial_code: this.dial_code,
      phoneNumber: this.phoneNumber.trim(),
      isMember: this.profileForm.get('isMember').value == 1,
      maritalStatus: this.profileForm.get('maritalStatus').value,
      occupation: this.profileForm.get('occupation').value == 0 ? null : this.profileForm.get('occupation').value,
      phoneNumberConfirmed: this.phoneConfirmed,
      phoneVerificationToken: this.phoneVerificationToken,
      emailConfirmed: this.accountActivated || (this.profileForm.get('email').value || '').trim() == "",
      language: this.profileForm.get('language').value,
      createdBy: this.user.email,
      extensionValues: this.getExtensions(),
      creationDate: new Date(),
      pictureUrl: 'assets/images/avatar.png',
    };
    this.profileSaving = true;

    this.profileService
      .setProfile(profile)
      .then(async (data) => {
        this.emailStatusVersion = String(Date.now());
        if (typeof data?.email === 'string' && data.email.toLowerCase() !== profile.email.toLowerCase()) {
          this.pendingEmailMessage = resourceText('CHECK_YOUR_NEW_EMAIL_TO_CONFIRM_THE_CHANGE_YOUR_PREVIOUS_ADDRESS_REMAINS');
          profile.email = data.email; profile.emailConfirmed = data.emailConfirmed;
          this.profileForm.patchValue({email:data.email});
        }
        if ((this.originalEmail.trim().toUpperCase() != profile.email.trim().toUpperCase()) &&
          (profile.email.trim() != "")) {
          this.accountActivated = false;
          profile.emailConfirmed = this.accountActivated;
          this.activationNeeded = true;
        }
        this.originalEmail = profile.email;
        this.accountActivated = profile.emailConfirmed;
        var primaryMember = this.memberArr.filter(
          (a) => a.isPrimary == true
        )[0];
        this.disableEmail = this.profileForm.get('disableEmail').value;
        this.disableText = this.profileForm.get('disableText').value;
        primaryMember.disableEmail = this.disableEmail;
        primaryMember.disableText = this.disableText;
        primaryMember.createdBy = profile.createdBy;
        primaryMember.creationDate = profile.creationDate;
        this.profileForm.patchValue({ createdBy: profile.createdBy });
        this.profileForm.patchValue({ creationDate: profile.creationDate });
        primaryMember = this.refreshAccountMember(profile, primaryMember);
        primaryMember.extensionValues = profile.extensionValues;
        await this.profileService.saveAccountMember(this.userId, primaryMember);
        await this.uploadPendingMemberPhoto(primaryMember);
        this.pictureUrl = primaryMember.pictureUrl;
        if (this.phoneConfirmed == false) {
          this.phoneVerificationClosed = false;
          this.codeSent = false;
        }
        this.refreshAccountHolder(
          profile.firstName,
          profile.lastName,
          profile.dateOfBirth,
          profile.isMember,
          profile.gender
        );
        if (this.shortDate == false) {
          this.user.language = profile.language;
          this._authService.saveLogin(this.user);
        }
        this.message = resourceText('PROFILE_UPDATED_318AFD3');
        if (this.householdEditor) {
          this.editingPrimary = false;
          this.acknowledgeHouseholdSave(primaryMember, 'updated');
        }
        this.profileForm.markAsPristine();
        this.profileSaving = false;
        this.isSuccess = true;
        this.successClosed = false;
        this.submit = true;
      })
      .catch((errorMessage) => {
        this.error = errorMessage?.error?.message || resourceText('UI_YOUR_CHANGES_COULD_NOT_BE_FULLY_SAVED_PLEASE_TRY_AGAIN');
        this.failureClosed = false;
        this.profileSaving = false;
        this.submit = true;
      });

    } finally {
      this.profileSaving = false;
    }
  }

  GetProfileSuccess() {
    return this.globalService.Get("PROFILE_UPDATED");
  }

  getExtensions() {
    let result: Extension[] = [];
    this.extensions.value.forEach(a => {
      let x: Extension = { key: a.id, value: a.value };
      result.push(x);
    });
    return result;
  }

  refreshAccountMember(profile: Profile, member: Member): Member {
    member.firstName = profile.firstName;
    member.lastName = profile.lastName;
    member.dateOfBirth = profile.dateOfBirth;
    member.gender = profile.gender;
    member.isMember = profile.isMember;
    member.emailAddress = profile.email;
    member.phoneNumber = profile.phoneNumber;
    member.maritalStatus = profile.maritalStatus;
    member.occupation = profile.occupation == 0 ? null : profile.occupation;
    return member;
  }

  refreshAccountHolder(
    firstName: string,
    lastName: string,
    dateOfBirth: Date,
    isMember: boolean,
    gender: boolean
  ) {
    var member = this.memberArr.filter((a) => a.isPrimary == true);
    if (member == null) return;
    member[0].firstName = firstName;
    member[0].lastName = lastName;
    member[0].dateOfBirth = dateOfBirth;
    member[0].isMember = isMember;
    member[0].gender = gender;
  }

  loadMemberByIndex(index: number): Member {
    let member: Member;
    member = {
      id: this.memberArr[index].id,
      firstName: this.memberArr[index].firstName.trim(),
      lastName: this.memberArr[index].lastName.trim(),
      dateOfBirth: this.memberArr[index].dateOfBirth,
      isMember: this.memberArr[index].isMember,
      gender: this.memberArr[index].gender,
      isPrimary: this.memberArr[index].isPrimary,
      relationship: this.memberArr[index].relationship,
      maritalStatus: this.memberArr[index].maritalStatus,
      occupation: this.memberArr[index].occupation == 0 ? null : this.memberArr[index].occupation,
      phoneNumber: this.memberArr[index].phoneNumber == null? "": this.memberArr[index].phoneNumber.trim(),
      emailAddress: this.memberArr[index].emailAddress == null? "": this.memberArr[index].emailAddress.trim(),
      hasAccount: this.memberArr[index].hasAccount,
      createdBy: this.memberArr[index].createdBy,
      creationDate: this.memberArr[index].creationDate,
      pictureUrl: 'assets/images/avatar.png',
      requestDescription: this.memberArr[index].requestDescription,
      noteCount: this.memberArr[index].noteCount,
      extensionValues: this.memberArr[index].extensionValues,
      disableText: this.memberArr[index].disableText,
      disableEmail: this.memberArr[index].disableEmail,
    };
    return member;
  }

  loadMemberByForm(form: NgForm, index: number): Member {
    if (index === undefined)
      return;
    const values = form.form?.getRawValue?.() || form.value;
    let member: Member;
    member = {
      id: values.id,
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      dateOfBirth: values.dateOfBirth === undefined ? this.memberArr[index].dateOfBirth : values.dateOfBirth,
      isMember: values.isMember,
      gender: values.gender,
      isPrimary: values.isPrimary,
      relationship: values.relationship,
      pictureUrl: 'assets/images/avatar.png',
      maritalStatus: values.maritalStatus,
      occupation: values.occupation == 0 ? null : values.occupation,
      phoneNumber: values.phoneNumber == null ? "": values.phoneNumber.trim(),
      hasAccount: values.hasAccount,
      emailAddress: values.emailAddress == null ? "": values.emailAddress.trim(),
      createdBy: this.user.email,
      creationDate: new Date(),
      requestDescription: '',
      noteCount: 0,
      extensionValues: this.memberArr[index].extensionValues,
      disableText: this.memberArr[index].disableText,
      disableEmail: this.memberArr[index].disableEmail,
    };
    return member;
  }

  isLastMember(form: any): boolean {
    return form.value.id == this.memberArr[this.memberArr.length - 1].id;
  }

  getCurIndex(id: number): number {
    let counter = 0;
    this.memberArr = this.memberArr.sort(this.sortByPrimaryAndName);
    for (counter = 0; counter < this.memberArr.length; counter++) {
      if (this.memberArr[counter].id == id) return counter;
    }
    return 0;
  }

  alreadyExists(member: Member): boolean {
    var result: boolean = false;
    this.memberArr.forEach((a) => {
      if (
        a.firstName.trim().toUpperCase() ==
        member.firstName.trim().toUpperCase() &&
        a.lastName.trim().toUpperCase() ==
        member.lastName.trim().toUpperCase() &&
        a.dateOfBirth && member.dateOfBirth && formatDate(a.dateOfBirth, 'yyyy-MM-dd', 'en') ==
        formatDate(member.dateOfBirth, 'yyyy-MM-dd', 'en')
      ) {
        this.memberError = 'DUPLICATE_INFORMATION';
        this.memberFailureClosed = false;
        result = true;
      }
    });
    return result;
  }

  async onSubmitMember(form: NgForm, indexOfMember: number) {
    if (this.memberSaving || this.profileSaving) return;
    this.curIndex = indexOfMember
    this.submitMember = true;
    this.memberSuccessClosed = true;
    this.memberFailureClosed = true;
    this.memberError = "";
    this.memberMessage = '';
    this.isMemberSuccess = false;
    let member: Member;
    member = this.loadMemberByForm(form, indexOfMember);
    if (this.fieldRequired('picture') && !this.pendingMemberPhotos.has(this.memberArr[indexOfMember]) && (!this.memberArr[indexOfMember].pictureUrl || this.memberArr[indexOfMember].pictureUrl.includes('assets/images/avatar.png'))) { this.memberError = resourceText('UI_PLEASE_ADD_A_PROFILE_PICTURE'); this.memberFailureClosed = false; return; }
    this.markIdValidity(form, this.extensionValid(member.extensionValues));
    if (
      !form.valid ||
      this.subdomain == 'demo' ||
      false
    ) {
      this.submitMember = true;
      return;
    }

    var mList: number[] = [];
    mList.push(member.id);
    var origMembership = this.memberArr[this.curIndex]['origIsMember'];
    var curMembership = this.memberArr[this.curIndex]['isMember'];
    member.extensionValues = this.memberArr[this.curIndex].extensionValues;

    if (
      this.workflow == true &&
      this.adminAccess == false &&
      origMembership != curMembership &&
      !(!origMembership && curMembership == false)
    ) {
      if (curMembership == true) {
        this.requestMembership(this.userId, mList);
        form.value.isMember = false;
        member.isMember = false;
        this.memberArr[this.curIndex].isMember = false;
        this.memberArr[this.curIndex].requestDescription =
          'MEMBERSHIP_REQUEST_SUBMITTED';
      } else {
        this.cancelMembership(this.userId, mList);
        form.value.isMember = true;
        member.isMember = true;
        this.memberArr[this.curIndex].isMember = true;
        this.memberArr[this.curIndex].requestDescription =
          'MEMBERSHIP_CANCELLATION_SUBMITTED';
      }
    }
    if (member.id > 0) {
      this.processSaving(form, member);
      return;
    }
    this.memberSaving = true;
    try {
      this.householdCandidates = await this.profileService.householdCandidates(this.userId, member);
      if (!this.householdCandidates.length) { await this.processSaving(form, member); return; }
      this.recommendationId = this.householdCandidates[0].id;
      this.recommendationMessage = resourceText('UI_A_PERSON_WITH_THIS_NAME_ALREADY_EXISTS_IN_YOUR_CONNECTED_HOUSEHOLD_IS_THIS_THE_SAME_PERSON_REUSING_THEIR_RECOR');
      this.recommendationClosed = false;
      this.recommendationForm = form;
      this.recommendationMember = member;
    } catch (error) {
      this.memberError = error?.error?.message || resourceText('UI_WE_COULD_NOT_CHECK_FOR_EXISTING_HOUSEHOLD_MEMBERS_PLEASE_TRY_AGAIN');
      this.memberFailureClosed = false;
    } finally { this.memberSaving = false; }
  }

  YesRecommendation(candidateId?: number) {
    this.recommendationClosed = true;
    this.recommendationMember.existingMemberId = candidateId || this.recommendationId;
    this.processSaving(this.recommendationForm, this.recommendationMember);
  }
  NoRecommendation() {
    this.recommendationClosed = true;
    this.recommendationMember.differentMemberIds = this.householdCandidates.map(person => person.id);
    this.processSaving(this.recommendationForm, this.recommendationMember);
  }
  LaterRecommendation() {
    this.recommendationClosed = true;
    this.processSaving(this.recommendationForm, this.recommendationMember);
  }

  extensionValid(extensionValues): boolean
  {
    if (extensionValues == null)
      return true;
    return extensionValues.filter(a => (a.mandatory == true && (a.value == null || a.value.trim() == ''))).length == 0;
  }

  async processSaving(form: any, member: Member) {
    try {
    let memberList: Member[] = [];
    this.memberSuccessClosed = true;
    this.memberFailureClosed = true;
    this.memberError = "";
    this.memberMessage = '';
    this.isMemberSuccess = false;
    memberList.push(member);
    if (this.extensionValid(member.extensionValues) == false)
    {
      form.valid = false;
      return;
    }
    this.memberSaving = true;
    //   this.curIndex = this.getCurIndex(member.id);
    if (form.value.id == 0) {
      // add new member
      await this.profileService
        .addMember(this.userId, memberList)
        .then(async (data) => {
          this.memberArr[this.curIndex].id = data[0].id;
          this.memberArr[this.curIndex].hasAccount = data[0].hasAccount;
          form.value.id = data[0].id;
          if (member.existingMemberId) {
            const pending = this.pendingMemberPhotos.get(this.memberArr[this.curIndex]);
            if (pending) URL.revokeObjectURL(pending.preview);
            this.pendingMemberPhotos.delete(this.memberArr[this.curIndex]);
            this.pendingMemberPhotoRemovals.delete(this.memberArr[this.curIndex]);
          } else await this.uploadPendingMemberPhoto(this.memberArr[this.curIndex]);
          memberList[0] = { ...memberList[0], ...data[0] };
          this.memberArr[this.curIndex].firstName = memberList[0].firstName;
          form.value.firstName = memberList[0].firstName;
          this.memberArr[this.curIndex].lastName = memberList[0].lastName;
          form.value.lastName = memberList[0].lastName;
          this.memberArr[this.curIndex].dateOfBirth = memberList[0].dateOfBirth ? new Date(memberList[0].dateOfBirth) : null;
          form.value.dateOfBirth = memberList[0].dateOfBirth ? new Date(memberList[0].dateOfBirth) : null;
          this.memberArr[this.curIndex].emailAddress =
            memberList[0].emailAddress;
          form.value.emailAddress = memberList[0].emailAddress;
          this.memberArr[this.curIndex].phoneNumber = memberList[0].phoneNumber;
          form.value.phoneNumber = memberList[0].phoneNumber;
          this.memberArr[this.curIndex].maritalStatus =
            memberList[0].maritalStatus;
          form.value.maritalStatus = memberList[0].maritalStatus;
          this.isMemberSuccess = true;
          this.memberSuccessClosed = false;
          this.memberArr[this.curIndex].createdBy = this.user.email;
          this.memberArr[this.curIndex].creationDate = new Date();
          form.value.createdBy = this.user.email;
          form.value.creationDate = new Date();
          form.form.markAsPristine();
          this.memberSaving = false;
          if (this.isLastMember(form)) this.newMemberNotSaved = false;
          if (member.existingMemberId) await this.LoadMembers();
          this.memberMessage = 'MEMBER_ADDED';
          this.acknowledgeHouseholdSave(memberList[0], 'added');
          this.editingHouseholdMemberId = null;
          this.submitMember = false;
          await this.refreshHouseholdDupes();
        })
        .catch((errorMessage) => {
          this.memberError = errorMessage?.error?.message || resourceText('UI_THE_MEMBER_COULD_NOT_BE_FULLY_SAVED_PLEASE_RETRY_SAVING_TO_COMPLETE_THE_PHOTO_UPLOAD');
          this.isMemberSuccess = false;
          this.memberFailureClosed = false;
          this.memberSaving = false;
          if (this.householdEditor && this.memberArr[this.curIndex]?.id > 0) this.editingHouseholdMemberId = this.memberArr[this.curIndex].id;
          this.submitMember = true;
        });
    } // update existing member
    else {
      await this.profileService
        .saveMember(this.userId, memberList[0])
        .then(async (data) => {
          this.memberArr[this.curIndex].id = data.id;
          form.value.id = data.id;
          await this.uploadPendingMemberPhoto(this.memberArr[this.curIndex]);
          this.memberArr[this.curIndex].firstName = memberList[0].firstName;
          form.value.firstName = memberList[0].firstName;
          this.memberArr[this.curIndex].lastName = memberList[0].lastName;
          form.value.lastName = memberList[0].lastName;
          if (this.shortDate == false)
            this.memberArr[this.curIndex].dateOfBirth = memberList[0].dateOfBirth ? new Date(memberList[0].dateOfBirth) : null;
          if (this.shortDate == false)
            form.value.dateOfBirth = memberList[0].dateOfBirth ? new Date(memberList[0].dateOfBirth) : null;
          this.memberArr[this.curIndex].emailAddress = data.emailAddress;
          form.value.emailAddress = data.emailAddress;
          this.memberArr[this.curIndex].phoneNumber = data.phoneNumber;
          form.value.phoneNumber = data.phoneNumber;
          this.memberArr[this.curIndex].maritalStatus = data.maritalStatus;
          form.value.maritalStatus = data.maritalStatus;
          this.memberArr[this.curIndex].createdBy = this.user.email;
          this.memberArr[this.curIndex].creationDate = new Date();
          form.value.createdBy = this.user.email;
          form.value.creationDate = new Date();
          form.form.markAsPristine();
          this.memberSaving = false;
          this.isMemberSuccess = true;
          if (this.isLastMember(form)) this.newMemberNotSaved = false;
          this.memberMessage = 'MEMBER_UPDATED';
          this.acknowledgeHouseholdSave(memberList[0], 'updated');
          this.editingHouseholdMemberId = null;
          this.submitMember = false;
          await this.refreshHouseholdDupes();
        })
        .catch((errorMessage) => {
          this.memberError = errorMessage?.error?.message || resourceText('UI_THE_MEMBER_COULD_NOT_BE_FULLY_SAVED_PLEASE_RETRY_SAVING_TO_COMPLETE_THE_PHOTO_CHANGE');
          this.isMemberSuccess = false;
          this.memberFailureClosed = false;
          this.memberSaving = false;
          this.submitMember = true;
        });
    }

    } finally {
      this.memberSaving = false;
    }
  }

  EditNote(member: number) {
    this._router.navigate(['/admin/notes/edit/' + member]);
  }

  ViewNotes(member: number) {
    this._router.navigate(['/admin/notes/view/' + member]);
  }

  MemberStatusHelp() {
    this.showMemberStatusTip = !this.showMemberStatusTip;
  }

  async OnAddNewMember() {

    try {
    this.submitMember = false;
    this.addingHouseholdMember = true;
    var Subscription = await this.appService.getSubscription();
    if (Subscription.memberActual >= Subscription.memberPlan) {
      this.subscriptionError = true;
      this.addingHouseholdMember = false;
      return;
    }
    if (this.subdomain == 'demo') return;
    this.memberSuccessClosed = true;
    this.memberFailureClosed = true;
    if (this.newMemberNotSaved == true) {
      this.curIndex = this.memberArr.length - 1;
      this.memberError = 'SAVE_BEFORE_ADD';
      this.memberFailureClosed = false;
      this.addingHouseholdMember = false;
      return;
    }
    this.newMemberNotSaved = true;
    this.memberSuccessClosed = true;
    this.memberFailureClosed = true;
    let member: Member;
    member = {
      id: 0,
      firstName: '',
      lastName: this.householdEditor ? this.profileForm.get('lastName')?.value || '' : '',
      dateOfBirth: null,
      isMember: this.memberArr.find(person => person.isPrimary)?.isMember === true,
      gender: null,
      isPrimary: false,
      relationship: null,
      emailAddress: '',
      phoneNumber: '',
      maritalStatus: null,
      occupation: null,
      hasAccount: false,
      pictureUrl: 'assets/images/avatar.png',
      requestDescription: '',
      noteCount: 0,
      disableEmail: false,
      disableText: false,
      extensionValues: null,
      createdBy: this.user.email,
      creationDate: new Date()
    };
    this.generateMemberOptions(member);
    this.memberArr.push(member);
    this.addingHouseholdMember = false;
    } finally { this.addingHouseholdMember = false; }
  }

  async ExecuteUnsubscribe() {
    if (this.submit == true)
      return;

    this.confirmUnsubscribe = false;
    this.profileUnsubscribe = true;
    var ret = await this.profileService.unsubscribe(this.userId);
    this.profileUnsubscribe = false;
    if (ret == true)
      this.authService.logout(1);
  }

  showConfirm(index: number) {
    this.curIndex = index;
    this.confirmDelete = true;
    this.isMemberSuccess = false;
  }

  async OnDeleteMember(index: number, memberId: number) {
    try {
    if (this.subdomain == 'demo') return;
    this.confirmDelete = false;
    this.memberSuccessClosed = true;
    this.memberFailureClosed = true;
    this.curIndex = index;
    this.successClosed = true;
    this.failureClosed = true;
    if (memberId == 0) {
      this.memberArr.splice(index, 1);
      this.newMemberNotSaved = false;
      return;
    }

    let member = this.loadMemberByIndex(index);
    let memberList: Member[] = [];
    memberList.push(member);
    this.memberDeleting = true;
    await this.profileService
      .deleteMember(this.userId, memberList)
      .then(() => {
        this.memberArr.splice(index, 1);
        this.memberDeleting = false;
      })
      .catch((errorMessage) => {
        this.memberError = errorMessage.message;
        this.memberFailureClosed = false;
        this.memberDeleting = false;
      });

    } finally {
      this.memberDeleting = false;
    }
  }
}
